// src/components/common/SourcesList.js
// Section sources - shows articles cited in a section

import React, { useState, useMemo } from 'react';
import './SourcesList.css';

// Known publication names by domain
const DOMAIN_MAP = {
  'reuters.com': 'Reuters',
  'bloomberg.com': 'Bloomberg',
  'apnews.com': 'Associated Press',
  'wsj.com': 'Wall Street Journal',
  'nytimes.com': 'New York Times',
  'cnbc.com': 'CNBC',
  'ft.com': 'Financial Times',
  'cnn.com': 'CNN',
  'bbc.com': 'BBC',
  'bbc.co.uk': 'BBC',
  'washingtonpost.com': 'Washington Post',
  'theguardian.com': 'The Guardian',
  'economist.com': 'The Economist',
  'politico.com': 'Politico',
  'axios.com': 'Axios',
  'thehill.com': 'The Hill',
  'npr.org': 'NPR',
  'cbc.ca': 'CBC News',
  'theglobeandmail.com': 'Globe and Mail',
  'youtube.com': 'YouTube',
  'wikipedia.org': 'Wikipedia',
  'whitehouse.gov': 'White House',
  'state.gov': 'State Department',
  'congress.gov': 'Congress.gov'
};

const getPublicationName = (domain) => {
  const clean = domain?.replace(/^www\./, '') || '';
  for (const [key, name] of Object.entries(DOMAIN_MAP)) {
    if (clean === key || clean.endsWith('.' + key)) return name;
  }
  return clean.split('.')[0].replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};

const getTitleFromUrl = (url) => {
  try {
    const urlObj = new URL(url);
    const segments = urlObj.pathname.split('/').filter(s => s && s.length > 0);
    if (segments.length === 0) return null;

    let slug = segments[segments.length - 1]
      .replace(/\.(html?|php|aspx?)$/i, '')
      .replace(/-\d{4}-\d{2}-\d{2}$/, '')
      .replace(/^\d{8}-/, '');

    if (/^\d+$/.test(slug) || slug.length < 5) {
      slug = segments.length > 1 ? segments[segments.length - 2] : null;
    }
    if (!slug) return null;

    const title = slug.replace(/[-_]/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).trim();
    return title.length > 3 ? title : null;
  } catch {
    return null;
  }
};

const SourcesList = ({ citations }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Convert citations map to array of article objects
  const articles = useMemo(() => {
    if (!citations || typeof citations !== 'object') return [];

    const seen = new Set();
    return Object.values(citations)
      .filter(url => {
        if (!url || seen.has(url)) return false;
        seen.add(url);
        return true;
      })
      .map(url => {
        let domain = null;
        try {
          domain = new URL(url).hostname.replace(/^www\./, '');
        } catch {
          return null;
        }

        return {
          url,
          domain,
          publication: getPublicationName(domain),
          title: getTitleFromUrl(url),
          faviconUrl: `https://www.google.com/s2/favicons?domain=${domain}&sz=32`
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.publication.localeCompare(b.publication));
  }, [citations]);

  if (articles.length === 0) return null;

  const count = articles.length;
  const maxVisible = 5;
  const hasOverflow = count > maxVisible;
  const displayArticles = hasOverflow ? articles.slice(0, maxVisible - 1) : articles.slice(0, maxVisible);
  const overflowCount = count - displayArticles.length;

  const handleToggle = (e) => {
    if (e.target.closest('a')) return;
    setIsExpanded(!isExpanded);
  };

  return (
    <div className={`sources-footer ${isExpanded ? 'sources-expanded' : ''}`}>
      <div
        className="sources-collapsed"
        onClick={handleToggle}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === 'Enter' && handleToggle(e)}
      >
        <div className="sources-favicons">
          {displayArticles.map((article, index) => (
            <span
              key={article.url}
              className="source-favicon-circle"
              style={{ zIndex: maxVisible - index }}
              title={article.publication}
            >
              <img
                src={article.faviconUrl}
                alt=""
                loading="lazy"
                decoding="async"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </span>
          ))}
          {hasOverflow && (
            <span
              className="source-favicon-circle source-overflow-count"
              style={{ zIndex: 0 }}
              title={`${overflowCount} more sources`}
            >
              +{overflowCount}
            </span>
          )}
        </div>
        <span className="sources-count">
          {count} {count === 1 ? 'source' : 'sources'}
        </span>
      </div>

      {isExpanded && (
        <div className="sources-expanded-list">
          {articles.map((article) => (
            <a
              key={article.url}
              href={article.url}
              target="_blank"
              rel="noopener noreferrer"
              className="source-expanded-item"
            >
              <img
                src={article.faviconUrl}
                alt=""
                className="source-expanded-favicon"
                loading="lazy"
                decoding="async"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
              <span className="source-expanded-text">
                <span className="source-expanded-publication">{article.publication}</span>
                {article.title && (
                  <>
                    <span className="source-expanded-separator"> — </span>
                    <span className="source-expanded-title">{article.title}</span>
                  </>
                )}
              </span>
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

export default SourcesList;
